import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

const RESETTING_CLASS = "is-route-resetting";

const useScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const restorePageVisibility = () => {
      document.documentElement.classList.remove(RESETTING_CLASS);
    };

    const hideBeforeInternalNavigation = (event) => {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const link = event.target.closest?.("a[href]");
      if (!link || link.target) return;

      const url = new URL(link.href, window.location.href);
      const isSameOrigin = url.origin === window.location.origin;
      const isSamePath = url.pathname === window.location.pathname;
      const isHashLink = Boolean(url.hash);

      if (!isSameOrigin || isHashLink || isSamePath) return;

      document.documentElement.classList.add(RESETTING_CLASS);
    };

    document.addEventListener("click", hideBeforeInternalNavigation, true);
    window.addEventListener("pageshow", restorePageVisibility);

    return () => {
      document.removeEventListener("click", hideBeforeInternalNavigation, true);
      window.removeEventListener("pageshow", restorePageVisibility);
    };
  }, []);

  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    document.documentElement.classList.add(RESETTING_CLASS);

    const html = document.documentElement;
    const body = document.body;
    const previousScrollBehavior = html.style.scrollBehavior;
    const previousBodyScrollBehavior = body.style.scrollBehavior;

    html.style.scrollBehavior = "auto";
    body.style.scrollBehavior = "auto";

    const target = hash && document.getElementById(hash.slice(1));
    if (target) {
      target.scrollIntoView({ block: "start" });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        html.style.scrollBehavior = previousScrollBehavior;
        body.style.scrollBehavior = previousBodyScrollBehavior;
        html.classList.remove(RESETTING_CLASS);
      });
    });
  }, [pathname, hash]);
};

export default useScrollToTop;
